import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  PhoneCall,
  Globe,
  Sparkles,
  Star,
  Shield,
  Quote
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { HERO as HERO_MOCK, FEATURES, TEAM_FEATURES, FAQS, NEWS, TRUST_BADGES, SITE, PLATFORM_PILLARS, GLOBAL_STATS, TRUST_PARTNERS, CLIENT_LOGOS } from '../data/mock';
import { usePageContent, getSectionFields } from '../hooks/usePageContent';
import ServicesGridBento from '../components/ServicesGridBento';
import CrmMarquee from '../components/CrmMarquee';
import FaqAccordion from '../components/FaqAccordion';
import CtaBanner from '../components/CtaBanner';
import Seo from '../components/Seo';

// Backend stores hero fields with snake_case keys (matching the admin v2
// PageSection schema). Map them onto the camelCase property names the
// existing Home.jsx hero block expects, so swapping the data source
// requires no JSX changes below. Anything missing in the API falls back
// to the legacy mock — never break the public page.
function mergeHero(apiHero) {
  if (!apiHero) return HERO_MOCK;
  return {
    ...HERO_MOCK,
    ...apiHero,
    titleHighlight: apiHero.title_highlight || apiHero.titleHighlight || HERO_MOCK.titleHighlight,
    primaryCta: apiHero.primary_cta || apiHero.primaryCta || HERO_MOCK.primaryCta,
    secondaryCta: apiHero.secondary_cta || apiHero.secondaryCta || HERO_MOCK.secondaryCta,
    // stats from API arrives as a `list` typed field whose value is an array;
    // mock has `stats` as an array directly. Trust the admin list when present.
    stats: Array.isArray(apiHero.stats) && apiHero.stats.length ? apiHero.stats : HERO_MOCK.stats,
  };
}

// Pluck a list-typed field's value out of a section's fields. Returns the
// array (possibly empty) or null if the section/field is missing.
function pluckListField(page, sectionType, fieldKey) {
  const fields = getSectionFields(page, sectionType);
  if (!fields) return null;
  const v = fields[fieldKey];
  return Array.isArray(v) ? v : null;
}

export default function Home() {
  const { page } = usePageContent('home');
  const apiHero = getSectionFields(page, 'hero');
  const HERO = mergeHero(apiHero);

  // Pillars, FAQs and CTA copy now come from the admin v2 page editor when
  // present. Each falls back to the legacy mock so the page always renders.
  const apiPillarsFields = getSectionFields(page, 'platform_pillars');
  const apiPillars = pluckListField(page, 'platform_pillars', 'items');
  const PILLARS = apiPillars && apiPillars.length ? apiPillars : PLATFORM_PILLARS;
  const pillarsHeading = apiPillarsFields?.section_title || 'Built on four engineering pillars';
  const pillarsSub = apiPillarsFields?.section_subtitle || 'Every feature you use sits on top of a globally distributed, observable and integration-ready platform.';
  const pillarsEyebrow = apiPillarsFields?.eyebrow || 'Platform Architecture';

  const apiFaqsFields = getSectionFields(page, 'faqs');
  const apiFaqs = pluckListField(page, 'faqs', 'items');
  const FAQS_DATA = apiFaqs && apiFaqs.length ? apiFaqs : FAQS;
  const faqsHeading = apiFaqsFields?.section_title || 'Frequently asked questions';
  const faqsSub = apiFaqsFields?.section_subtitle || '';
  const faqsEyebrow = apiFaqsFields?.eyebrow || 'FAQs';

  const apiCta = getSectionFields(page, 'cta_banner');

  const apiServicesFields = getSectionFields(page, 'services_grid');
  const apiServices = pluckListField(page, 'services_grid', 'items');

  // Remote Work split section — every field is admin-editable, but the
  // important new one is `video` which falls back to a static image when
  // empty. The admin can switch a section between "image" and "video"
  // without code changes.
  const remote = getSectionFields(page, 'remote_work') || {};
  const remoteEyebrow = remote.eyebrow || 'Remote Work';
  const remoteTitle = remote.title || 'Access your desk phone from anywhere';
  const remoteSubtitle = remote.subtitle || "Get the most out of your desk phone and stay connected with your business from anywhere. VoIPinger gives you all the desk-phone features remotely — at home, on the road or in another country.";
  const remoteVideo = remote.video || '/assets/videos/voipinger-manage-your-phone.mp4';
  const remoteImage = remote.image || '/assets/voip-phone-features-Edited-768x474.png';
  const remoteVideoPoster = remote.video_poster || remoteImage;
  const remoteBadgeTitle = remote.badge_title || 'Work from anywhere';
  const remoteBadgeText = remote.badge_text || 'Take your office phone home with you — same extension, any device.';

  // How VoIPinger works section — same image-vs-video pattern. Falls back
  // to defaults so the page renders before the seed has populated the DB.
  const howItWorks = getSectionFields(page, 'how_it_works') || {};
  const hiwEyebrow = howItWorks.eyebrow || 'How VoIPinger works';
  const hiwTitle = howItWorks.title || 'Cloud telephony, simplified';
  const hiwSubtitle = howItWorks.subtitle || 'VoIP converts calls into digital data packets transported over the internet using SIP and RTP protocols. The result: rich features, lower costs and zero hardware lock-in.';
  const hiwVideo = howItWorks.video || '/assets/videos/voipinger-how-it-works.mp4';
  const hiwImage = howItWorks.image || '/assets/VoIP-Business-Phone-Systems.png';
  const hiwVideoPoster = howItWorks.video_poster || hiwImage;

  return (
    <>
      <Seo
        title="VoIPinger | Cloud Hosted PBX, Microsoft Teams Direct Routing, Contact Centre"
        description="Carrier-grade cloud telephony from the UK. Cloud Hosted PBX, Microsoft Teams Direct Routing (TeamMate), DID + Toll-Free numbers in 145+ countries, contact-centre with AI sentiment analysis. 99.99% uptime SLA."
        path="/"
        faq={FAQS_DATA}
        breadcrumbs={[{ name: 'Home', path: '/' }]}
      />
      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden mesh-bg text-white">
        <div className="absolute inset-0 dot-pattern opacity-40" />
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] bg-fuchsia-500/15 rounded-full blur-3xl animate-pulse-slow" />

        <div className="relative container-x grid lg:grid-cols-12 items-center gap-10 lg:gap-16">
          <div className="lg:col-span-7 animate-fade-in-up">
            <span className="eyebrow-dark mb-6">
              <Sparkles size={12} /> {HERO.badge}
            </span>
            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
              {HERO.title}
              <br />
              <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-violet-200 bg-clip-text text-transparent">
                {HERO.titleHighlight}
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-violet-50/85 leading-relaxed max-w-xl">
              {HERO.subtitle}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary justify-center">
                {HERO.primaryCta} <ArrowRight size={16} />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {HERO.stats.map((s) => (
                <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 backdrop-blur-md p-4">
                  <p className="text-2xl md:text-3xl font-extrabold bg-gradient-to-br from-violet-300 to-fuchsia-200 bg-clip-text text-transparent">
                    {s.value}
                  </p>
                  <p className="text-xs text-violet-100/70 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative animate-float">
              <div className="absolute inset-0 -m-6 rounded-3xl bg-gradient-to-br from-violet-400/30 to-fuchsia-500/20 blur-2xl" />
              <div className="relative rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-3 shadow-[0_30px_80px_-30px_rgba(6,182,212,0.5)]">
                <img
                  src={HERO.image}
                  alt="Business VoIP phone solution"
                  className="rounded-2xl w-full h-auto"
                  loading="eager"
                />
              </div>
              {/* Floating badges */}
              <div className="hidden md:flex absolute -left-8 top-10 items-center gap-2 bg-white text-slate-900 px-3 py-2 rounded-xl shadow-2xl">
                <span className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
                  <PhoneCall size={16} />
                </span>
                <div className="text-xs">
                  <p className="font-bold">HD Call active</p>
                  <p className="text-slate-500">Crystal clear audio</p>
                </div>
              </div>
              <div className="hidden md:flex absolute -right-6 bottom-12 items-center gap-2 bg-white text-slate-900 px-3 py-2 rounded-xl shadow-2xl">
                <span className="w-8 h-8 rounded-lg bg-fuchsia-100 text-fuchsia-600 flex items-center justify-center">
                  <Shield size={16} />
                </span>
                <div className="text-xs">
                  <p className="font-bold">Enterprise grade</p>
                  <p className="text-slate-500">SSL encrypted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES STRIP */}
      <section className="relative -mt-10 z-10">
        <div className="container-x">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-900/5 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {TRUST_BADGES.map((b) => {
              const Ic = Icons[b.icon] || Icons.Star;
              return (
                <div key={b.title} className="flex items-center gap-4 p-6">
                  <span className="w-12 h-12 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
                    <Ic size={20} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Why VoIPinger</p>
                    <p className="font-bold text-slate-900">{b.title}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CARRIER-GRADE PLATFORM — trust strip */}
      <section className="bg-white pt-14 md:pt-16">
        <div className="container-x">
          <div className="rounded-3xl bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white px-6 md:px-10 py-8 md:py-10 shadow-2xl shadow-violet-500/30 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-12 w-72 h-72 rounded-full bg-fuchsia-300/20 blur-3xl pointer-events-none" />

            <div className="relative grid lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-[11px] font-bold uppercase tracking-widest mb-3">
                  <Icons.ShieldCheck size={11} /> Carrier-grade platform
                </span>
                <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">
                  Built on the infrastructure <span className="bg-white/15 px-2 rounded">global carriers</span> trust.
                </h3>
                <p className="text-white/85 text-sm md:text-base mt-3 max-w-md leading-relaxed">
                  Three Tier-4 datacentres, 99.99% uptime SLA and active-active geo-redundancy.
                  Enterprise-grade telephony engineered for businesses of every size.
                </p>
                <Link
                  to="/platform"
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-violet-700 text-sm font-bold hover:bg-violet-50 transition-colors shadow-md"
                >
                  See the platform <Icons.ArrowRight size={14} />
                </Link>
              </div>

              <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { v: '99.99%', l: 'Uptime SLA' },
                  { v: '3', l: 'Tier-4 datacentres' },
                  { v: '145+', l: 'Countries served' },
                  { v: '24/7', l: 'UK-based NOC' }
                ].map((s) => (
                  <div key={s.l} className="rounded-2xl bg-white/15 backdrop-blur border border-white/20 px-4 py-4 text-center">
                    <p className="text-2xl md:text-3xl font-extrabold">{s.v}</p>
                    <p className="text-[11px] uppercase tracking-wider text-white/80 mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED BY LOGOS */}
      <section className="bg-white">
        <div className="container-x pt-14 pb-2">
          <p className="text-center text-xs font-bold tracking-widest text-slate-400 uppercase mb-6">
            Trusted by ambitious teams across the UK & beyond
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-6 items-center justify-items-center">
            {CLIENT_LOGOS.map((c) => (
              <span
                key={c.name}
                className="font-bold tracking-widest text-slate-400 hover:text-violet-600 transition-colors text-sm md:text-base"
              >
                {c.text}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS & SERVICES */}
      <ServicesGridBento
        items={apiServices}
        eyebrow={apiServicesFields?.eyebrow}
        title={apiServicesFields?.section_title}
        subtitle={apiServicesFields?.section_subtitle}
      />

      {/* PLATFORM PILLARS */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-violet-50/40 to-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">{pillarsEyebrow}</span>
            <h2 className="section-title mt-4">{pillarsHeading}</h2>
            <p className="section-sub mt-3">{pillarsSub}</p>
            <div className="divider-svg mt-6" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p) => {
              const Ic = Icons[p.icon] || Icons.Sparkles;
              return (
                <div key={p.title} className="card-modern relative overflow-hidden group">
                  <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-violet-100 to-fuchsia-100 opacity-60 group-hover:scale-110 transition-transform" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-lg shadow-violet-500/30">
                      <Ic size={22} />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900 mb-2">{p.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CRM INTEGRATIONS */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Telephony CRM Integration</span>
            <h2 className="section-title mt-4">Plays nicely with 250+ CRMs</h2>
            <p className="section-sub mt-3">
              Sync contacts, log calls automatically and trigger workflows in your favourite CRM —
              without writing a single line of code.
            </p>
          </div>
          <CrmMarquee />
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/crm-integrations" className="btn-light">
              See all integrations <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="btn-primary">
              Talk to a specialist <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* REMOTE WORK / FEATURE SPLIT */}
      <section className="bg-white">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-violet-100 to-fuchsia-50 -z-10" />
            {remoteVideo ? (
              <video
                src={remoteVideo}
                poster={remoteVideoPoster}
                className="rounded-2xl shadow-xl w-full"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={remoteTitle}
              />
            ) : (
              <img
                src={remoteImage}
                alt={remoteTitle}
                className="rounded-2xl shadow-xl w-full"
                loading="lazy"
              />
            )}
            <div className="hidden md:block absolute -bottom-8 -right-6 bg-white rounded-2xl shadow-2xl border border-slate-100 p-5 w-60">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center">
                  <Globe size={18} />
                </span>
                <p className="font-bold">{remoteBadgeTitle}</p>
              </div>
              <p className="text-xs text-slate-500">
                {remoteBadgeText}
              </p>
            </div>
          </div>

          <div>
            <span className="eyebrow">{remoteEyebrow}</span>
            <h2 className="section-title mt-4">{remoteTitle}</h2>
            <p className="section-sub mt-4">
              {remoteSubtitle}
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {[
                'Special function keys',
                'Speed dialling',
                'Smart call forwarding',
                'Busy-light & presence',
                'Voicemail-2-Email',
                '+40 dashboard features'
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-700">
                  <Check size={16} className="text-violet-600 mt-1" />
                  <span className="text-sm">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/web-phone" className="btn-primary">
                Explore Softphone <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-light">
                Book a demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="bg-slate-50">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Why choose us</span>
            <h2 className="section-title mt-4">Everything a modern business needs</h2>
            <p className="section-sub mt-3">
              Powerful out of the box, simple to manage and ready to scale with your team.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div key={f.title} className="card-modern group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-lg shadow-violet-500/30 group-hover:scale-110 transition-transform">
                    <Ic size={20} />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — copy + video/image admin-editable via the
          /admin/pages/home → "How VoIPinger works" section. */}
      <section className="bg-white">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">{hiwEyebrow}</span>
            <h2 className="section-title mt-4">{hiwTitle}</h2>
            <p className="section-sub mt-4">
              {hiwSubtitle}
            </p>
            <div className="mt-6 space-y-4">
              {[
                { n: '01', t: 'Sign up & verify', d: 'Create your account in minutes — no contracts, no setup fees.' },
                { n: '02', t: 'Provision a number', d: 'Pick a new UK or international number, or port your existing one.' },
                { n: '03', t: 'Configure your dashboard', d: 'Set IVRs, queues, routing and integrations from a single portal.' },
                { n: '04', t: 'Start taking calls', d: 'Use your desk phone, computer, mobile or web — anywhere, anytime.' }
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-4">
                  <span className="font-extrabold text-violet-600 text-2xl">{s.n}</span>
                  <div>
                    <p className="font-bold text-slate-900">{s.t}</p>
                    <p className="text-sm text-slate-600">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-tr from-violet-100 via-fuchsia-50 to-white rounded-3xl -z-10" />
            {hiwVideo ? (
              <video
                src={hiwVideo}
                poster={hiwVideoPoster}
                className="rounded-2xl w-full shadow-xl"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={hiwTitle}
              />
            ) : (
              <img
                src={hiwImage}
                alt={hiwTitle}
                className="rounded-2xl w-full"
                loading="lazy"
              />
            )}
          </div>
        </div>
      </section>

      {/* TEAM COLLABORATION */}
      <section className="relative overflow-hidden bg-[#0d0a1f] text-white">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="absolute -top-40 -left-32 w-96 h-96 bg-violet-500/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-32 w-96 h-96 bg-fuchsia-500/15 rounded-full blur-3xl" />

        <div className="relative container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow-dark">Team Collaboration</span>
            <h2 className="section-title section-title-dark mt-4">Built for the way modern teams work</h2>
            <p className="section-sub text-violet-50/80 mt-3">
              Bring conversations together with chat, SMS and video — without leaving the platform.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TEAM_FEATURES.map((t) => {
              const Ic = Icons[t.icon] || Icons.MessageCircle;
              return (
                <div key={t.title} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 hover:bg-white/10 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-violet-500/20 text-violet-300 flex items-center justify-center mb-4">
                    <Ic size={22} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t.title}</h3>
                  <p className="text-sm text-violet-50/70">{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL COVERAGE */}
      <section className="relative overflow-hidden bg-white">
        <div className="container-x section-pad">
          <div className="relative rounded-3xl mesh-bg p-8 md:p-14 overflow-hidden text-white">
            <div className="absolute inset-0 dot-pattern opacity-25" />
            <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-violet-500/15 blur-3xl" />
            <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-fuchsia-500/15 blur-3xl" />

            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="eyebrow-dark mb-4">Global Coverage</span>
                <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4">
                  A network that reaches as far as your business does
                </h2>
                <p className="text-violet-100/85 text-lg leading-relaxed">
                  Direct interconnects with Tier-1 carriers in 145+ countries, geo-redundant SBCs, and a 24/7 NOC keep your calls flowing wherever your team and customers happen to be.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/wholesale-voip" className="btn-primary">Explore the network <ArrowRight size={16} /></Link>
                  <Link to="/managed-infrastructure" className="btn-ghost">Infrastructure</Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {GLOBAL_STATS.map((s) => (
                  <div key={s.label} className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur">
                    <p className="text-4xl font-extrabold bg-gradient-to-r from-violet-300 via-fuchsia-300 to-purple-200 bg-clip-text text-transparent">{s.value}</p>
                    <p className="text-sm text-violet-100/80 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Customer Stories</span>
            <h2 className="section-title mt-4">Trusted by teams across the UK & beyond</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Emma Carter',
                role: 'Operations Manager, Linden Estates',
                msg: 'We migrated 32 lines in a weekend. Calls are crystal clear and the dashboard is the cleanest we’ve used.'
              },
              {
                name: 'Daniel Reyes',
                role: 'Founder, Reyes Logistics',
                msg: 'The CRM integration with HubSpot saved our agents hours every week. No more copy-pasting numbers.'
              },
              {
                name: 'Priya Sharma',
                role: 'Head of Support, Northstar Tech',
                msg: 'Vicidial inside VoIPinger transformed our outbound campaigns. Reporting and AMD work flawlessly.'
              }
            ].map((t) => (
              <div key={t.name} className="card-modern">
                <Quote size={28} className="text-violet-500/40 mb-4" />
                <p className="text-slate-700 leading-relaxed mb-5">{t.msg}</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white font-bold flex items-center justify-center">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
                <div className="flex gap-1 mt-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={12} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS / BLOG */}
      <section className="bg-slate-50 border-t border-slate-100">
        <div className="container-x section-pad">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="eyebrow">From the blog</span>
              <h2 className="section-title mt-4">Latest news & guides</h2>
            </div>
            <Link to="/blog" className="btn-light">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {NEWS.map((n) => (
              <article key={n.title} className="card-modern p-0 overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={n.img}
                    alt={n.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-wider text-violet-600 font-semibold mb-2">{n.date}</p>
                  <h3 className="font-bold text-slate-900 mb-2 leading-snug">{n.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">{n.excerpt}</p>
                  <Link to="/blog" className="text-violet-600 font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">
                    Read more <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE / TRUST STRIP */}
      <section className="bg-white border-t border-slate-100">
        <div className="container-x py-14">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {TRUST_PARTNERS.map((t) => {
              const Ic = Icons[t.icon] || Icons.Shield;
              return (
                <div
                  key={t.name}
                  className="flex items-center gap-3 bg-slate-50 hover:bg-violet-50 transition-colors rounded-xl border border-slate-200 px-4 py-3.5"
                >
                  <span className="w-9 h-9 rounded-lg bg-white text-violet-600 border border-slate-200 flex items-center justify-center flex-shrink-0">
                    <Ic size={16} />
                  </span>
                  <span className="font-semibold text-slate-700 text-xs leading-tight">{t.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqAccordion
        items={FAQS_DATA}
        eyebrow={faqsEyebrow}
        title={faqsHeading}
        subtitle={faqsSub || 'Answers to the questions we hear most often. Need more? Our team is on standby.'}
      />

      {/* CTA BANNER */}
      <CtaBanner
        eyebrow={apiCta?.eyebrow}
        title={apiCta?.title}
        subtitle={apiCta?.subtitle}
        primaryLabel={apiCta?.primary_label}
        primaryHref={apiCta?.primary_href}
        secondaryLabel={apiCta?.secondary_label}
        secondaryHref={apiCta?.secondary_href}
      />
    </>
  );
}
